import React, { memo } from "react";
import { motion } from "framer-motion";
import {
  FaCalendarAlt,
  FaPhoneAlt,
  FaStar,
  FaShieldAlt,
  FaQuoteLeft,
  FaTooth,
  FaUserMd,
  FaSmile
} from "react-icons/fa";
import smileLogo from "../assets/smilelogo.png";

const drQuote = {
  text: "Creating Healthy Smiles with Compassion, Precision & Trust.",
  author: "Dr. Sainulabid",
  title: "Chief Dental Surgeon"
};

const TrustBadge = memo(({ rating = "4.7", patientCount = "5000+" }) => (
  <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mb-6 sm:mb-8">
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.7, type: "spring" }}
      className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all"
    >
      <FaStar className="text-yellow-500 text-sm sm:text-base" />
      <span className="text-xs sm:text-sm font-semibold text-gray-700">Trusted Dental Care</span>
    </motion.div>
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.8, type: "spring" }}
      className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all"
    >
      <FaShieldAlt className="text-teal-500 text-sm sm:text-base" />
      <span className="text-xs sm:text-sm font-semibold text-gray-700">Trusted by Families</span>
    </motion.div>
  </div>
));

export default function Hero() {
  const highlights = [
    { icon: <FaTooth className="text-sm sm:text-base" />, text: "Modern Technology" },
    { icon: <FaUserMd className="text-sm sm:text-base" />, text: "Experienced Doctors" },
    { icon: <FaSmile className="text-sm sm:text-base" />, text: "Hollywood Smile" },
    { icon: <FaShieldAlt className="text-sm sm:text-base" />, text: "Sterile Environment" }
  ];

  return (
    <section id="home" className="relative flex items-center justify-center min-h-[85vh] md:min-h-screen overflow-hidden pt-[110px] sm:pt-[130px] md:pt-[150px] pb-12 sm:pb-16">

      <div className="absolute inset-0 bg-gradient-to-br from-[#F4F8F9] via-[#F9FCFC] to-[#E6F7F8]">

        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-teal-100/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-blue-100/15 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/40 rounded-full blur-3xl"></div>
        </div>

        <motion.div
          animate={{
            y: [0, -25, 0],
            rotate: [0, 8, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-24 left-6 sm:left-10 opacity-[0.08] sm:opacity-10"
        >
          <FaTooth className="text-4xl sm:text-6xl text-teal-400" />
        </motion.div>

        <motion.div
          animate={{
            y: [0, 25, 0],
            rotate: [0, -8, 0]
          }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-24 right-6 sm:right-10 opacity-[0.08] sm:opacity-10"
        >
          <FaTooth className="text-4xl sm:text-6xl text-teal-400" />
        </motion.div>

        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute top-1/2 left-1/4 opacity-[0.05] sm:opacity-[0.06] hidden sm:block"
        >
          <FaTooth className="text-5xl text-teal-400" />
        </motion.div>

        <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(to_right,#2CA6B1_1px,transparent_1px),linear-gradient(to_bottom,#2CA6B1_1px,transparent_1px)] bg-[size:60px_60px]"></div>

        <motion.div
          animate={{
            x: [0, 50, 0, -50, 0],
            y: [0, -30, 0, 30, 0],
            scale: [1, 1.1, 1, 0.9, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-10 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-teal-100/10 rounded-full blur-2xl"
        />

        <motion.div
          animate={{
            x: [0, -60, 0, 60, 0],
            y: [0, 40, 0, -40, 0],
            scale: [1, 0.9, 1, 1.1, 1]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-0 left-0 w-[350px] sm:w-[600px] h-[350px] sm:h-[600px] bg-blue-100/10 rounded-full blur-2xl"
        />

        <motion.div
          animate={{
            x: [0, 30, 0, -30, 0],
            y: [0, -20, 0, 20, 0],
            scale: [1, 1.05, 1, 0.95, 1]
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[700px] h-[400px] sm:h-[700px] bg-teal-50/10 rounded-full blur-2xl"
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto text-center px-4 sm:px-6 lg:px-8">

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            type: "spring",
            stiffness: 150,
            damping: 20
          }}
          className="flex justify-center mb-4 sm:mb-6"
        >
          <motion.div
            animate={{
              y: [0, -4, 0, -2, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              repeatDelay: 1
            }}
            className="relative"
          >
            <motion.div
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.08, 0.18, 0.08]
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-[-6px] sm:inset-[-8px] bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400 rounded-full blur-lg sm:blur-lg"
            />

            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              className="absolute inset-[-4px] sm:inset-[-6px] rounded-full"
              style={{
                background: "conic-gradient(from 0deg, #2CA6B1, #4FD1C5, #2CA6B1, #4FD1C5, #2CA6B1)"
              }}
            />

            <motion.div
              animate={{
                scale: [1, 1.04, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 bg-gradient-to-r from-teal-400/20 to-teal-600/20 rounded-full blur-sm sm:blur-md"
            />

            <motion.img
              src={smileLogo}
              alt="Dr. Sainulabid's IQRA Multispeciality Dental Clinic - Best Dentist in Athavanad, Malappuram"
              className="relative w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain rounded-full bg-white shadow-xl p-1.5 sm:p-2"
              whileHover={{
                scale: 1.08,
                rotate: 5,
                boxShadow: "0 10px 30px -5px rgba(44,166,177,0.4)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
            />
          </motion.div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 sm:mb-3 md:mb-4 leading-[1.15]"
        >
          <span className="bg-gradient-to-r from-[#168A94] to-[#0F6C74] bg-clip-text text-transparent">
            Best Dental Clinic in Athavanad, Malappuram
          </span>
        </motion.h1>

        <motion.div
          initial={{ width: 0 }}
          animate={{ width: "5rem" }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="h-1 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] mx-auto rounded-full mb-3 sm:mb-4"
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.22 }}
          className="text-gray-500 text-xs sm:text-sm font-medium mb-2 sm:mb-3 tracking-wide"
        >
          Advanced Multispeciality Dental Care for the Whole Family
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.24 }}
          className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4 sm:mb-5"
        >
          {highlights.map((item, idx) => (
            <motion.span
              key={idx}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 + idx * 0.05, type: "spring" }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-full text-[10px] sm:text-xs font-medium text-gray-700 shadow-sm"
            >
              <span className="text-teal-500">{item.icon}</span>
              {item.text}
            </motion.span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="flex justify-center mb-3 sm:mb-4"
        >
          <div className="relative inline-block w-full max-w-[340px] sm:max-w-md md:max-w-xl">
            <div className="relative group">
              <div className="absolute inset-0 bg-white/40 backdrop-blur-xl rounded-2xl border border-white/50 shadow-2xl shadow-teal-500/10"></div>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-50/30 to-white/10"></div>
              <motion.div
                className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 pointer-events-none"
                initial={{ x: "-100%" }}
                whileHover={{ x: "100%" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              />
              <div className="relative px-4 sm:px-6 py-3 sm:py-4 rounded-2xl">
                <span className="inline-block w-full text-center text-[1.1rem] sm:text-xl md:text-2xl lg:text-3xl font-bold text-[#168A94] leading-snug sm:leading-tight">
                  Dr. Sainulabid's IQRA Multispeciality Dental Clinic
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.28 }}
          className="text-gray-600 text-[10px] sm:text-xs md:text-sm mb-3 sm:mb-4 px-2"
        >
          Dental Implants <span className="text-gray-300">•</span> Root Canal <span className="text-gray-300">•</span> Hollywood Smile Design <span className="text-gray-300">•</span> Braces & Aligners <span className="text-gray-300">•</span> Family Dental Care
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex justify-center mb-5 sm:mb-7"
        >
          <motion.div
            animate={{
              y: [0, -3, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              repeatDelay: 2
            }}
            className="relative max-w-xl mx-auto px-2 sm:px-0 w-full"
          >
            <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl border border-teal-100" />
            <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-r from-teal-200/20 to-teal-100/20" />
            <div className="absolute inset-0 rounded-xl sm:rounded-2xl shadow-[0_0_30px_rgba(44,166,177,0.05)]" />
            <div className="relative px-4 sm:px-6 md:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl border border-white/40">

              <div className="flex justify-center mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-teal-50 flex items-center justify-center border border-teal-100">
                  <FaQuoteLeft className="text-teal-500 text-sm sm:text-base" />
                </div>
              </div>

              <p className="text-[#374151] text-xs sm:text-sm md:text-base leading-relaxed text-center px-1 sm:px-2 font-medium">
                "{drQuote.text}"
              </p>

              <div className="flex items-center justify-center gap-3 mt-3 sm:mt-4">
                <div className="flex-1 max-w-[60px] h-px bg-gradient-to-r from-transparent to-teal-300"></div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-teal-600 font-semibold text-[10px] sm:text-xs md:text-sm">
                    {drQuote.author}
                  </span>
                  <span className="text-gray-300 text-[8px] sm:text-xs">•</span>
                  <span className="text-gray-500 text-[8px] sm:text-[10px] md:text-xs">
                    {drQuote.title}
                  </span>
                </div>
                <div className="flex-1 max-w-[60px] h-px bg-gradient-to-l from-transparent to-teal-300"></div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* ✅ Cleaner Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="text-gray-600 text-[11px] sm:text-xs md:text-sm max-w-lg mx-auto mb-4 sm:mb-6 px-3"
        >
          <span className="font-semibold text-teal-600">
            Dr. Sainulabid's IQRA Multispeciality Dental Clinic
          </span>{" "}
          in{" "}
          <span className="font-semibold text-teal-600">
            Athavanad
          </span>{" "}
          provides advanced multispeciality dental care with modern technology and a patient-first approach, serving families across Athavanad, Kattilangadi, Kurumbathur, Valanchery, Kuttippuram, Pattarnadakavu, Thirunavaya, Puthanathani and nearby areas.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-5 sm:mb-7"
        >
          <div className="inline-flex flex-wrap items-center justify-center gap-1.5 sm:gap-3 bg-white shadow-lg border border-gray-200 px-3 sm:px-5 py-1.5 sm:py-2 rounded-full">
            <FaTooth className="text-teal-500 text-xs sm:text-sm md:text-base" />
            <span className="text-gray-700 text-[10px] sm:text-xs md:text-sm font-medium text-center">
              Modern Dentistry <span className="text-gray-300 hidden xs:inline">•</span>
              <span className="inline xs:hidden"> </span>
              Gentle Care <span className="text-gray-300 hidden xs:inline">•</span>
              <span className="inline xs:hidden"> </span>
              Beautiful Smiles
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <TrustBadge />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4 sm:px-0"
        >
          <a
            href="#contact"
            className="group relative w-full sm:w-auto sm:min-w-[200px] px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-[#168A94] to-[#0F6C74] text-white font-semibold text-xs sm:text-sm md:text-base shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 text-center overflow-hidden inline-flex items-center justify-center gap-2"
          >
            <FaCalendarAlt className="text-xs sm:text-sm md:text-base" />
            <span>Book Appointment</span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12"
              initial={{ x: "-150%" }}
              whileHover={{ x: "150%" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute inset-0 bg-white/20 rounded-xl"
              initial={{ scale: 0, opacity: 0 }}
              whileTap={{ scale: 1.5, opacity: 0.3 }}
              transition={{ duration: 0.3 }}
            />
            <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-[#168A94]/20 to-[#0F6C74]/20 blur-xl" />
          </a>

          <a
            href="tel:+918590300370"
            className="group relative w-full sm:w-auto sm:min-w-[200px] px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl border-2 border-[#168A94] text-[#168A94] font-semibold text-xs sm:text-sm md:text-base hover:bg-[#168A94] hover:text-white transition-all duration-300 text-center overflow-hidden bg-white/50 backdrop-blur-sm inline-flex items-center justify-center gap-2"
          >
            <FaPhoneAlt className="text-xs sm:text-sm md:text-base" />
            <span>Call Now</span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-teal-200/30 to-transparent -skew-x-12"
              initial={{ x: "-150%" }}
              whileHover={{ x: "150%" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute inset-0 bg-teal-100/30 rounded-xl"
              initial={{ scale: 0, opacity: 0 }}
              whileTap={{ scale: 1.5, opacity: 0.2 }}
              transition={{ duration: 0.3 }}
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
}